import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import Calendar from '../components/Calendar';

import { API_BASE_URL } from '../config';

const CompanyAdministration = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hr-payroll');
  const [operatingExpenses, setOperatingExpenses] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [otherExpenses, setOtherExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // HR and Payroll Management States
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payrollItems, setPayrollItems] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState({});

  // Attendance Management States
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceForm, setAttendanceForm] = useState({
    employeeId: '',
    attendanceDate: new Date().toISOString().split('T')[0],
    status: 'present',
    checkInTime: '',
    checkOutTime: '',
    workingHours: 8,
    overtimeHours: 0,
    leaveType: '',
    isHalfDay: false,
    reason: ''
  });

  // Form states for payroll management
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    designation: '',
    department: '',
    email: '',
    phone: '',
    joiningDate: new Date().toISOString().split('T')[0],
    basicSalary: '',
    notes: '',
    createPortalUser: false,
    portalRole: 'employee',
    // Salary structure
    hra: '',
    da: '',
    conveyance: '',
    medical: '',
    specialAllowance: '',
    bonus: '',
    otherAllowances: '',
    pf: '',
    tds: '',
    professionalTax: '',
    otherDeductions: ''
  });

  // State for pending calendar changes
  const [pendingCalendarChanges, setPendingCalendarChanges] = useState({});
  const [calendarSaved, setCalendarSaved] = useState(false);

  // Form states
  const [operatingExpenseForm, setOperatingExpenseForm] = useState({
    expenseCategory: '',
    expenseName: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    vendor: '',
    referenceNumber: '',
    description: ''
  });

  const [otherIncomeForm, setOtherIncomeForm] = useState({
    incomeCategory: '',
    incomeName: '',
    amount: '',
    incomeDate: new Date().toISOString().split('T')[0],
    source: '',
    referenceNumber: '',
    description: ''
  });

  const [otherExpenseForm, setOtherExpenseForm] = useState({
    expenseCategory: '',
    expenseName: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    vendor: '',
    referenceNumber: '',
    description: ''
  });

  // Expense categories
  const expenseCategoriesData = {
    operatingExpenses: [
      { value: 'rent', label: 'Rent' },
      { value: 'utilities', label: 'Utilities' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'office_supplies', label: 'Office Supplies' },
      { value: 'insurance', label: 'Insurance' },
      { value: 'depreciation', label: 'Depreciation' },
      { value: 'maintenance', label: 'Maintenance' },
      { value: 'travel', label: 'Travel' },
      { value: 'legal', label: 'Legal' },
      { value: 'accounting', label: 'Accounting' },
      { value: 'other', label: 'Other' }
    ],
    otherIncome: [
      { value: 'interest_income', label: 'Interest Income' },
      { value: 'dividend_income', label: 'Dividend Income' },
      { value: 'foreign_exchange_gain', label: 'Foreign Exchange Gain' },
      { value: 'rental_income', label: 'Rental Income' },
      { value: 'commission_income', label: 'Commission Income' },
      { value: 'other', label: 'Other' }
    ],
    otherExpenses: [
      { value: 'interest_expense', label: 'Interest Expense' },
      { value: 'foreign_exchange_loss', label: 'Foreign Exchange Loss' },
      { value: 'penalties', label: 'Penalties' },
      { value: 'donations', label: 'Donations' },
      { value: 'other', label: 'Other' }
    ]
  };

  useEffect(() => {
    setExpenseCategories(expenseCategoriesData);
    fetchData();
    fetchPayrollData();
    fetchAttendanceData();
  }, [selectedMonth, selectedYear]);

  // HR and Payroll Management Functions
  const fetchPayrollData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [employeesRes, payrollItemsRes, payrollSummaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/payroll/employees`, { headers }),
        axios.get(`${API_BASE_URL}/payroll/payroll-items?month=${selectedMonth}&year=${selectedYear}`, { headers }),
        axios.get(`${API_BASE_URL}/payroll/payroll-summary?month=${selectedMonth}&year=${selectedYear}`, { headers })
      ]);

      console.log('Employees loaded:', employeesRes.data);
      setEmployees(employeesRes.data);
      setPayrollItems(payrollItemsRes.data);
      setPayrollSummary(payrollSummaryRes.data);
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      toast.error('Failed to load payroll data');
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();

    // Validate portal user creation
    if (employeeForm.createPortalUser && !employeeForm.email) {
      toast.error('Email is required when creating a portal user account');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (isEditMode && editingEmployeeId) {
        // Update existing employee
        await axios.put(`${API_BASE_URL}/payroll/employees/${editingEmployeeId}`, employeeForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Employee updated successfully!');
      } else {
        // Create new employee
        await axios.post(`${API_BASE_URL}/payroll/employees`, employeeForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(
          employeeForm.createPortalUser
            ? `Employee added successfully! Portal user created with email: ${employeeForm.email}`
            : 'Employee added successfully!'
        );
      }

      // Reset form and edit mode
      setEmployeeForm({
        name: '', designation: '', department: '', email: '', phone: '',
        joiningDate: new Date().toISOString().split('T')[0], basicSalary: '', notes: '',
        createPortalUser: false, portalRole: 'employee',
        hra: '', da: '', conveyance: '', medical: '', specialAllowance: '',
        bonus: '', otherAllowances: '', pf: '', tds: '', professionalTax: '', otherDeductions: ''
      });
      setIsEditMode(false);
      setEditingEmployeeId(null);
      fetchPayrollData();
    } catch (error) {
      toast.error(error.response?.data?.error || (isEditMode ? 'Failed to update employee' : 'Failed to add employee'));
    }
  };

  const handleEditEmployee = (employee) => {
    setEmployeeForm({
      name: employee.name,
      designation: employee.designation,
      department: employee.department,
      email: employee.email,
      phone: employee.phone,
      joiningDate: employee.joiningDate ? new Date(employee.joiningDate).toISOString().split('T')[0] : '',
      basicSalary: employee.basicSalary?.toString() || '',
      notes: employee.notes || '',
      createPortalUser: false,
      portalRole: 'employee',
      hra: employee.salaryStructure?.hra?.toString() || '',
      da: employee.salaryStructure?.da?.toString() || '',
      conveyance: employee.salaryStructure?.conveyance?.toString() || '',
      medical: employee.salaryStructure?.medical?.toString() || '',
      specialAllowance: employee.salaryStructure?.specialAllowance?.toString() || '',
      bonus: employee.salaryStructure?.bonus?.toString() || '',
      otherAllowances: employee.salaryStructure?.otherAllowances?.toString() || '',
      pf: employee.salaryStructure?.pf?.toString() || '',
      tds: employee.salaryStructure?.tds?.toString() || '',
      professionalTax: employee.salaryStructure?.professionalTax?.toString() || '',
      otherDeductions: employee.salaryStructure?.otherDeductions?.toString() || ''
    });
    setIsEditMode(true);
    setEditingEmployeeId(employee.id);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/payroll/employees/${employeeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Employee deleted successfully!');
        fetchPayrollData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete employee');
      }
    }
  };

  const handleProcessPayroll = async () => {
    try {
      const token = localStorage.getItem('token');
      const employeeIds = employees.map(emp => emp.id);

      await axios.post(`${API_BASE_URL}/payroll/process-payroll`, {
        month: selectedMonth,
        year: selectedYear,
        employeeIds
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Payroll processed successfully!');
      fetchPayrollData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process payroll');
    }
  };

  const handleApprovePayroll = async (payrollId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/payroll/approve-payroll/${payrollId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Payroll approved successfully!');
      fetchPayrollData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve payroll');
    }
  };

  const handlePaySalary = async (payrollId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/payroll/pay-salary/${payrollId}`, {
        paymentMethod: 'bank_transfer',
        referenceNumber: `PAY-${Date.now()}`,
        notes: 'Salary payment processed'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Salary paid successfully!');
      fetchPayrollData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to pay salary');
    }
  };

  // Attendance Management Functions
  const fetchAttendanceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      console.log('Fetching attendance data for month:', selectedMonth, 'year:', selectedYear);

      const [attendanceRes, monthlyAttendanceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/payroll/attendance?month=${selectedMonth}&year=${selectedYear}`, { headers }),
        axios.get(`${API_BASE_URL}/payroll/attendance/monthly?month=${selectedMonth}&year=${selectedYear}`, { headers })
      ]);

      console.log('Attendance records received:', attendanceRes.data);
      console.log('Monthly attendance received:', monthlyAttendanceRes.data);

      // Debug: Log the first few attendance records to see the date format
      if (attendanceRes.data.length > 0) {
        console.log('Sample attendance record:', attendanceRes.data[0]);
        console.log('Sample attendance date type:', typeof attendanceRes.data[0].attendanceDate);
        console.log('Sample attendance date value:', attendanceRes.data[0].attendanceDate);
      }

      setAttendanceRecords(attendanceRes.data);
      setMonthlyAttendance(monthlyAttendanceRes.data);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      toast.error('Failed to load attendance data');
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/payroll/attendance`, attendanceForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Attendance recorded successfully!');
      setAttendanceForm({
        employeeId: '',
        attendanceDate: new Date().toISOString().split('T')[0],
        status: 'present',
        checkInTime: '',
        checkOutTime: '',
        workingHours: 8,
        overtimeHours: 0,
        leaveType: '',
        isHalfDay: false,
        reason: ''
      });
      fetchAttendanceData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record attendance');
    }
  };

  const handleCalculateMonthlyAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const employeeIds = employees.map(emp => emp.id);

      await axios.post(`${API_BASE_URL}/payroll/attendance/calculate-monthly`, {
        month: selectedMonth,
        year: selectedYear,
        employeeIds
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Monthly attendance calculated successfully!');
      fetchAttendanceData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to calculate monthly attendance');
    }
  };

  const handleQuickAttendanceUpdate = (dateStr, status) => {
    console.log('handleQuickAttendanceUpdate called with:', { dateStr, status, employeeId: attendanceForm.employeeId });

    if (!attendanceForm.employeeId) {
      toast.error('Please select an employee first');
      return;
    }

    const newStatus = status;
    console.log('Setting status to:', newStatus);

    // Update pending changes
    setPendingCalendarChanges(prev => ({
      ...prev,
      [dateStr]: {
        employeeId: attendanceForm.employeeId,
        attendanceDate: dateStr,
        status: newStatus,
        checkInTime: newStatus === 'present' ? '09:00' : '',
        checkOutTime: newStatus === 'present' ? '18:00' : '',
        workingHours: newStatus === 'present' ? 8 : 0,
        overtimeHours: 0,
        leaveType: newStatus === 'leave' ? 'casual' : '',
        isHalfDay: newStatus === 'half_day',
        reason: ''
      }
    }));

    // Update local attendance records for immediate visual feedback
    const existingRecordIndex = attendanceRecords.findIndex(record =>
      record.employeeId === attendanceForm.employeeId &&
      record.attendanceDate === dateStr
    );

    const updatedRecord = {
      employeeId: attendanceForm.employeeId,
      attendanceDate: dateStr,
      status: newStatus,
      checkInTime: newStatus === 'present' ? '09:00' : '',
      checkOutTime: newStatus === 'present' ? '18:00' : '',
      workingHours: newStatus === 'present' ? 8 : 0,
      overtimeHours: 0,
      leaveType: newStatus === 'leave' ? 'casual' : '',
      isHalfDay: newStatus === 'half_day',
      reason: '',
      employee: employees.find(emp => emp.id === attendanceForm.employeeId)
    };

    if (existingRecordIndex >= 0) {
      const updatedRecords = [...attendanceRecords];
      updatedRecords[existingRecordIndex] = updatedRecord;
      setAttendanceRecords(updatedRecords);
    } else {
      setAttendanceRecords(prev => [...prev, updatedRecord]);
    }

    toast.success(`Attendance updated: ${newStatus}`);
  };

  const handleSaveCalendar = async () => {
    try {
      const token = localStorage.getItem('token');
      const attendanceData = Object.values(pendingCalendarChanges);

      if (attendanceData.length === 0) {
        toast.error('No changes to save');
        return;
      }

      await axios.post(`${API_BASE_URL}/payroll/attendance/bulk`, attendanceData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setPendingCalendarChanges({});
      setCalendarSaved(true);
      toast.success(`✅ Calendar saved successfully! ${attendanceData.length} attendance records saved.`);

      // Convert attendance records to calendar events
      try {
        const startDate = new Date(selectedYear, selectedMonth - 1, 1).toISOString();
        const endDate = new Date(selectedYear, selectedMonth, 0).toISOString();

        await axios.post(`${API_BASE_URL}/calendar/events/convert-attendance`, {
          employeeId: attendanceForm.employeeId,
          startDate,
          endDate
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        console.log('Attendance records converted to calendar events');
      } catch (convertError) {
        console.error('Error converting attendance to calendar events:', convertError);
        // Don't show error to user as this is optional
      }

      // Refresh attendance data to sync with server
      setTimeout(() => {
        fetchAttendanceData();
      }, 500);
    } catch (error) {
      console.error('Error saving calendar:', error);
      toast.error('Failed to save calendar');
    }
  };

  const handleResetChanges = () => {
    setPendingCalendarChanges({});
    setCalendarSaved(false);
    fetchAttendanceData(); // Refresh to get original data
    toast.success('Changes reset successfully');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch operating expenses
      const operatingExpensesResponse = await axios.get(`${API_BASE_URL}/operating-expenses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { month: selectedMonth, year: selectedYear }
      });
      setOperatingExpenses(operatingExpensesResponse.data);

      // Fetch other income
      const otherIncomeResponse = await axios.get(`${API_BASE_URL}/other-income`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { month: selectedMonth, year: selectedYear }
      });
      setOtherIncome(otherIncomeResponse.data);

      // Fetch other expenses
      const otherExpensesResponse = await axios.get(`${API_BASE_URL}/other-expenses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { month: selectedMonth, year: selectedYear }
      });
      setOtherExpenses(otherExpensesResponse.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOperatingExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/operating-expenses`, operatingExpenseForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Operating expense added successfully!');
      setOperatingExpenseForm({
        expenseCategory: '',
        expenseName: '',
        amount: '',
        expenseDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'bank_transfer',
        vendor: '',
        referenceNumber: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error adding operating expense:', error);
      toast.error('Failed to add operating expense');
    }
  };

  const handleOtherIncomeSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/other-income`, otherIncomeForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Other income added successfully!');
      setOtherIncomeForm({
        incomeCategory: '',
        incomeName: '',
        amount: '',
        incomeDate: new Date().toISOString().split('T')[0],
        source: '',
        referenceNumber: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error adding other income:', error);
      toast.error('Failed to add other income');
    }
  };

  const handleOtherExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/other-expenses`, otherExpenseForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Other expense added successfully!');
      setOtherExpenseForm({
        expenseCategory: '',
        expenseName: '',
        amount: '',
        expenseDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'bank_transfer',
        vendor: '',
        referenceNumber: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error adding other expense:', error);
      toast.error('Failed to add other expense');
    }
  };

  const deleteOperatingExpense = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/operating-expenses/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Operating expense deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting operating expense:', error);
      toast.error('Failed to delete operating expense');
    }
  };

  const deleteOtherIncome = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/other-income/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Other income deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting other income:', error);
      toast.error('Failed to delete other income');
    }
  };

  const deleteOtherExpense = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/other-expenses/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Other expense deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting other expense:', error);
      toast.error('Failed to delete other expense');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full py-6 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Company Administration</h1>
          <p className="mt-2 text-gray-600">
            Manage operating expenses, other income, and other expenses for your company.
          </p>
        </div>

        {/* Month/Year Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={1}>January</option>
              <option value={2}>February</option>
              <option value={3}>March</option>
              <option value={4}>April</option>
              <option value={5}>May</option>
              <option value={6}>June</option>
              <option value={7}>July</option>
              <option value={8}>August</option>
              <option value={9}>September</option>
              <option value={10}>October</option>
              <option value={11}>November</option>
              <option value={12}>December</option>
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'hr-payroll', label: 'HR & Payroll' },
                { id: 'attendance', label: 'Attendance' },
                { id: 'calendar', label: 'Calendar' },
                { id: 'operating-expenses', label: 'Operating Expenses' },
                { id: 'other-income', label: 'Other Income' },
                { id: 'other-expenses', label: 'Other Expenses' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* HR & Payroll Tab */}
            {activeTab === 'hr-payroll' && (
              <div className="space-y-6">
                {/* Employee Management */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">
                    {isEditMode ? 'Edit Employee' : 'Add New Employee'}
                  </h3>
                  <form onSubmit={handleEmployeeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name *</label>
                      <input
                        type="text"
                        placeholder="Enter employee name"
                        value={employeeForm.name}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                      <input
                        type="text"
                        placeholder="Enter designation"
                        value={employeeForm.designation}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, designation: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                      <input
                        type="text"
                        placeholder="Enter department"
                        value={employeeForm.department}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={employeeForm.email}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        placeholder="Enter phone number"
                        value={employeeForm.phone}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date *</label>
                      <input
                        type="date"
                        value={employeeForm.joiningDate}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, joiningDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary *</label>
                      <input
                        type="number"
                        placeholder="Enter basic salary"
                        value={employeeForm.basicSalary}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, basicSalary: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        placeholder="Enter any additional notes"
                        value={employeeForm.notes}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>

                    {/* Portal User Creation Section */}
                    <div className="md:col-span-2 border-t pt-4 mt-4">
                      <h4 className="text-md font-medium text-gray-900 mb-3">Portal Access</h4>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="createPortalUser"
                            checked={employeeForm.createPortalUser}
                            onChange={(e) => setEmployeeForm({ ...employeeForm, createPortalUser: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="createPortalUser" className="ml-2 block text-sm text-gray-900">
                            Create portal user account for this employee
                          </label>
                        </div>

                        {employeeForm.createPortalUser && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-blue-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Portal Role *</label>
                              <select
                                value={employeeForm.portalRole}
                                onChange={(e) => setEmployeeForm({ ...employeeForm, portalRole: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required={employeeForm.createPortalUser}
                              >
                                <option value="employee">Employee</option>
                                <option value="sales_executive">Sales Executive</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                This will determine the employee's access level in the portal
                              </p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-md">
                              <p className="text-sm text-blue-800">
                                <strong>Login Details:</strong><br />
                                Email: {employeeForm.email || 'Employee email'}<br />
                                Password: <span className="font-mono">changeme123</span><br />
                                <span className="text-xs">(Employee should change password on first login)</span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 md:col-span-2"
                    >
                      {isEditMode ? 'Update Employee' : 'Add Employee'}
                    </button>
                  </form>
                </div>

                {/* Employees List */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Employees</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joining Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Basic Salary</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Portal Access</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {employees.map((employee) => (
                          <tr key={employee.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.designation}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.department}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.phone}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-IN') : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.basicSalary ? `₹${employee.basicSalary.toLocaleString()}` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee.portalUser ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                                  {employee.portalUser.role}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                                  No Access
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => handleEditEmployee(employee)}
                                className="text-blue-600 hover:text-blue-900 mr-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteEmployee(employee.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payroll Processing */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Payroll Processing</h3>
                  </div>
                  <div className="p-6">
                    <button
                      onClick={handleProcessPayroll}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Process Payroll for {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                    </button>
                  </div>
                </div>

                {/* Payroll Summary */}
                {payrollSummary.totalEmployees > 0 && (
                  <div className="bg-white rounded-lg border">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold">Payroll Summary</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-600">Total Employees</h4>
                          <p className="text-2xl font-bold text-blue-900">{payrollSummary.totalEmployees}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-green-600">Total Salary</h4>
                          <p className="text-2xl font-bold text-green-900">₹{payrollSummary.totalSalary?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-yellow-600">Processed</h4>
                          <p className="text-2xl font-bold text-yellow-900">{payrollSummary.processedCount || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
              <div className="space-y-6">
                {/* Attendance Form */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Record Attendance</h3>
                  <form onSubmit={handleAttendanceSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={attendanceForm.employeeId}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, employeeId: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name} - {employee.designation}
                        </option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={attendanceForm.attendanceDate}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, attendanceDate: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <select
                      value={attendanceForm.status}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="leave">Leave</option>
                      <option value="half_day">Half Day</option>
                    </select>
                    <input
                      type="time"
                      placeholder="Check In Time"
                      value={attendanceForm.checkInTime}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, checkInTime: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="time"
                      placeholder="Check Out Time"
                      value={attendanceForm.checkOutTime}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, checkOutTime: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="number"
                      placeholder="Working Hours"
                      value={attendanceForm.workingHours}
                      onChange={(e) => setAttendanceForm({ ...attendanceForm, workingHours: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 md:col-span-2"
                    >
                      Record Attendance
                    </button>
                  </form>
                </div>

                {/* Attendance Records */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Attendance Records</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check Out</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Working Hours</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {attendanceRecords.map((record) => (
                          <tr key={record.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.employee?.name || 'Unknown Employee'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(record.attendanceDate).toLocaleDateString('en-IN')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${record.status === 'present' ? 'bg-green-100 text-green-800' :
                                record.status === 'absent' ? 'bg-red-100 text-red-800' :
                                  record.status === 'leave' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.checkInTime || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.checkOutTime || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.workingHours || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Calendar Events</h3>
                  <p className="text-gray-600 mb-4">
                    View and manage all calendar events including attendance, meetings, leaves, and other events.
                  </p>
                  <Calendar />
                </div>
              </div>
            )}

            {/* Operating Expenses Tab */}
            {activeTab === 'operating-expenses' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Add Operating Expense</h3>
                  <form onSubmit={handleOperatingExpenseSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={operatingExpenseForm.expenseCategory}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, expenseCategory: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Category</option>
                      {expenseCategories.operatingExpenses?.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Expense Name"
                      value={operatingExpenseForm.expenseName}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, expenseName: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={operatingExpenseForm.amount}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, amount: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="date"
                      placeholder="Expense Date"
                      value={operatingExpenseForm.expenseDate}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, expenseDate: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <select
                      value={operatingExpenseForm.paymentMethod}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, paymentMethod: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="online">Online</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Vendor"
                      value={operatingExpenseForm.vendor}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, vendor: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      placeholder="Reference Number"
                      value={operatingExpenseForm.referenceNumber}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, referenceNumber: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <textarea
                      placeholder="Description"
                      value={operatingExpenseForm.description}
                      onChange={(e) => setOperatingExpenseForm({ ...operatingExpenseForm, description: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
                      rows={3}
                    />
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 md:col-span-2"
                    >
                      Add Operating Expense
                    </button>
                  </form>
                </div>

                {/* Operating Expenses List */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Operating Expenses</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {operatingExpenses.map((expense) => (
                          <tr key={expense.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.expenseCategory}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.expenseName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(expense.amount)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(expense.expenseDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.paymentMethod}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.vendor}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => deleteOperatingExpense(expense.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Other Income Tab */}
            {activeTab === 'other-income' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Add Other Income</h3>
                  <form onSubmit={handleOtherIncomeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={otherIncomeForm.incomeCategory}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, incomeCategory: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Category</option>
                      {expenseCategories.otherIncome?.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Income Name"
                      value={otherIncomeForm.incomeName}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, incomeName: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={otherIncomeForm.amount}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, amount: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="date"
                      placeholder="Income Date"
                      value={otherIncomeForm.incomeDate}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, incomeDate: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Source"
                      value={otherIncomeForm.source}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, source: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      placeholder="Reference Number"
                      value={otherIncomeForm.referenceNumber}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, referenceNumber: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <textarea
                      placeholder="Description"
                      value={otherIncomeForm.description}
                      onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, description: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
                      rows={3}
                    />
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 md:col-span-2"
                    >
                      Add Other Income
                    </button>
                  </form>
                </div>

                {/* Other Income List */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Other Income</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {otherIncome.map((income) => (
                          <tr key={income.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{income.incomeCategory}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{income.incomeName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(income.amount)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(income.incomeDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{income.source}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => deleteOtherIncome(income.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Other Expenses Tab */}
            {activeTab === 'other-expenses' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Add Other Expense</h3>
                  <form onSubmit={handleOtherExpenseSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={otherExpenseForm.expenseCategory}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, expenseCategory: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Category</option>
                      {expenseCategories.otherExpenses?.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Expense Name"
                      value={otherExpenseForm.expenseName}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, expenseName: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={otherExpenseForm.amount}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, amount: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <input
                      type="date"
                      placeholder="Expense Date"
                      value={otherExpenseForm.expenseDate}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, expenseDate: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                    <select
                      value={otherExpenseForm.paymentMethod}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, paymentMethod: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="online">Online</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Vendor"
                      value={otherExpenseForm.vendor}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, vendor: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      placeholder="Reference Number"
                      value={otherExpenseForm.referenceNumber}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, referenceNumber: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <textarea
                      placeholder="Description"
                      value={otherExpenseForm.description}
                      onChange={(e) => setOtherExpenseForm({ ...otherExpenseForm, description: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
                      rows={3}
                    />
                    <button
                      type="submit"
                      className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 md:col-span-2"
                    >
                      Add Other Expense
                    </button>
                  </form>
                </div>

                {/* Other Expenses List */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Other Expenses</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {otherExpenses.map((expense) => (
                          <tr key={expense.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.expenseCategory}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.expenseName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(expense.amount)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(expense.expenseDate)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.paymentMethod}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.vendor}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => deleteOtherExpense(expense.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAdministration;
